import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import {
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  ArrowBack as ArrowBackIcon,
  Tag as TagIcon,
  Timer as TimerIcon,
  CalendarToday as CalendarIcon,
  Update as UpdateIcon,
} from '@mui/icons-material';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Shift, Driver, Assignment, ComplianceReport, ShiftStatus } from '../types';
import { getShiftDurationHours, formatDateTime, isWithinShiftTimeWindow } from '../utils';
import { cityOptions } from '../../../constants/enums';
import { FETCH_SHIFT, ACTIVATE_SHIFT, CANCEL_SHIFT } from '../../../api/mutations/ShiftManagementMutations';
import { stagingOptions } from '../../../constants/apollo';

const ShiftDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: shiftData, loading: shiftLoading, error: shiftError, refetch: refetchShift } = useQuery(FETCH_SHIFT, {
    ...stagingOptions,
    variables: { id: id || '' },
    skip: !id,
    fetchPolicy: 'network-only',
    onError: (error) => {
      console.error('Error fetching shift:', error);
    },
  });

  const [activateShiftMutation, { loading: activating }] = useMutation(ACTIVATE_SHIFT, {
    ...stagingOptions,
  });

  const [cancelShiftMutation, { loading: cancelling }] = useMutation(CANCEL_SHIFT, {
    ...stagingOptions,
  });

  const shift: Shift | undefined = useMemo(() => {
    if (!shiftData?.fetchShift) {
      return undefined;
    }

    try {
      const apiShift = shiftData.fetchShift;
      return {
        id: apiShift.id || '',
        name: apiShift.name || (apiShift.id ? `Shift ${apiShift.id}` : 'Unknown Shift'),
        startTime: apiShift.startTime || '',
        endTime: apiShift.endTime || '',
        requiredDrivers: apiShift.requiredDrivers || 1,
        maxDrivers: apiShift.requiredDrivers || 1,
        status: (apiShift.status?.toLowerCase() || 'draft') as ShiftStatus,
        startLatitude: apiShift.startLatitude,
        startLongitude: apiShift.startLongitude,
        assignedDriversCount: apiShift.shiftAssignments?.length || 0,
        availableSlots: (apiShift.requiredDrivers || 1) - (apiShift.shiftAssignments?.length || 0),
        durationHours: apiShift.durationHours,
        cityId: apiShift.cityId || apiShift.city?.id || undefined,
        neighborhoodId: apiShift.neighborhood?.id || undefined,
        zone: apiShift.neighborhood?.name || undefined,
        shiftTemplate: apiShift.shiftTemplate || undefined,
        createdAt: apiShift.createdAt,
        updatedAt: apiShift.updatedAt,
      };
    } catch (error) {
      console.error('Error transforming shift data:', error);
      return undefined;
    }
  }, [shiftData]);

  const assignments: Assignment[] = useMemo(() => {
    if (!shiftData?.fetchShift?.shiftAssignments) {
      return [];
    }

    return shiftData.fetchShift.shiftAssignments.map((assignment: any) => ({
      id: assignment.id,
      shiftId: shift?.id || '',
      driverId: assignment.courier?.id || '',
      courierId: assignment.courier?.id || '',
      status: (assignment.status?.toLowerCase() || 'pending') as Assignment['status'],
      checkedInAt: assignment.checkedInAt || undefined,
      createdAt: assignment.createdAt || undefined,
      assignedAt: assignment.assignedAt || undefined,
    }));
  }, [shiftData, shift]);

  const drivers: Driver[] = useMemo(() => {
    if (!shiftData?.fetchShift?.shiftAssignments) {
      return [];
    }

    return shiftData.fetchShift.shiftAssignments.map((assignment: any) => {
      const courier = assignment.courier;

      return {
        id: courier?.id || '',
        firstName: courier?.firstName || '',
        lastName: courier?.lastName || '',
        name: courier?.firstName && courier?.lastName
          ? `${courier.firstName} ${courier.lastName}`
          : courier?.firstName || courier?.lastName || 'Unknown',
        mobileNumber: courier?.mobileNumber || '',
        phone: courier?.mobileNumber || '',
      };
    });
  }, [shiftData]);

  const complianceReports: ComplianceReport[] = useMemo(() => {
    if (!shiftData?.fetchShift?.shiftDriverStats) {
      return [];
    }

    return shiftData.fetchShift.shiftDriverStats.map((stat: any) => ({
      id: `report-${stat.courierId}`,
      driverId: stat.courierId,
      shiftId: shift?.id || '',
      onlineDurationHours: stat.onlineHours || 0,
      acceptanceRate: stat.acceptanceRate || 0,
      compliant: stat.compliant !== undefined ? stat.compliant : true,
    }));
  }, [shiftData, shift]);

  if (shiftLoading) {
    return (
      <div className="main-wrapper">
        <div className="w-full px-6 lg:px-8 py-8 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <CircularProgress size={48} className="mb-4" />
            <p className="text-gray-600">Loading shift details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (shiftError || (!shift && !shiftLoading)) {
    return (
      <div className="main-wrapper">
        <div className="w-full px-6 lg:px-8 py-8">
          <button
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/shift-management');
              }
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-primaryColor transition-colors mb-6 group"
          >
            <ArrowBackIcon
              sx={{ fontSize: '20px' }}
              className="group-hover:translate-x-[-2px] transition-transform"
            />
            <span className="text-sm font-medium">Back</span>
          </button>

          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">
              {shiftError ? 'Error loading shift details' : 'Shift not found'}
            </h2>
            {shiftError && (
              <div className="text-sm text-red-600 mb-4">
                <p className="font-medium mb-2">Error details:</p>
                <p>{shiftError.message || 'An error occurred while loading the shift details. Please try again.'}</p>
                {shiftError.graphQLErrors && shiftError.graphQLErrors.length > 0 && (
                  <ul className="list-disc list-inside mt-2">
                    {shiftError.graphQLErrors.map((err: any, idx: number) => (
                      <li key={idx}>{err.message}</li>
                    ))}
                  </ul>
                )}
                {process.env.NODE_ENV === 'development' && (
                  <pre className="mt-2 text-xs bg-red-100 p-2 rounded overflow-auto">
                    {JSON.stringify(shiftError, null, 2)}
                  </pre>
                )}
              </div>
            )}
            {!shiftError && !shift && (
              <p className="text-sm text-red-600 mb-4">
                The shift you're looking for doesn't exist or has been deleted.
              </p>
            )}
            <button
              onClick={() => navigate('/shift-management')}
              className="px-4 py-2 bg-primaryColor text-white rounded-md hover:bg-darkBlue transition-colors"
            >
              Go to Shift Management
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!shift) {
    return null;
  }

  const getAssignedDriversForShift = (shiftId: string) => {
    return assignments.filter((a) => a.shiftId === shiftId).map((a) => a.driverId);
  };

  const assignedDriverIds = getAssignedDriversForShift(shift.id);
  const assignedDrivers = drivers.filter((d) => assignedDriverIds.includes(d.id));
  const shiftAssignments = assignments.filter((a) => a.shiftId === shift.id);
  const shiftComplianceReports = complianceReports.filter((r) => r.shiftId === shift.id);

  const shiftDuration = shift.durationHours || getShiftDurationHours(shift);
  const maxDrivers = shift.requiredDrivers || shift.maxDrivers || 0;

  const compliantCount = shiftData?.fetchShift?.shiftDriverStats?.filter(
    (stat: any) => stat.compliant === true
  ).length || 0;
  const totalDriverStats = shiftData?.fetchShift?.shiftDriverStats?.length || 0;
  const nonCompliantCount = totalDriverStats - compliantCount;
  const complianceRate = totalDriverStats > 0
    ? Math.round((compliantCount / totalDriverStats) * 100)
    : 0;

  const statusColor = shift.status === 'active'
    ? 'from-green-500 via-green-600 to-green-700'
    : shift.status === 'completed'
      ? 'from-blue-500 via-blue-600 to-blue-700'
      : 'from-gray-500 via-gray-600 to-gray-700';

  return (
    <div className="flex flex-col space-y-2 min-h-screen bg-gray-50">
      <div className="main-wrapper">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">

          <button
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/shift-management');
              }
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-primaryColor transition-colors mb-4 group"
          >
            <ArrowBackIcon
              sx={{ fontSize: '18px' }}
              className="group-hover:translate-x-[-2px] transition-transform"
            />
            <span className="text-sm font-medium">Back</span>
          </button>

          <div className="mb-4">
            <div className={`bg-gradient-to-r ${statusColor} rounded-xl p-4 lg:p-6 shadow-lg relative overflow-hidden`}>
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -mr-24 -mt-24"></div>
              <div className="absolute bottom-0 left-0 w-36 h-36 bg-white/5 rounded-full -ml-18 -mb-18"></div>
              <div className="relative z-10">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center shadow-lg border border-white/30">
                      <ScheduleIcon sx={{ fontSize: '24px', color: 'white' }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h1 className="text-2xl lg:text-3xl font-bold text-white drop-shadow-lg">{shift.name}</h1>
                        <span className="px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold text-white border border-white/30">
                          {shift.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-white/95">
                        <div className="flex items-center gap-1.5">
                          <ScheduleIcon sx={{ fontSize: '16px' }} />
                          <span className="font-medium">{formatDateTime(shift.startTime)}</span>
                        </div>
                        <span className="text-white/70">→</span>
                        <div className="flex items-center gap-1.5">
                          <ScheduleIcon sx={{ fontSize: '16px' }} />
                          <span className="font-medium">{formatDateTime(shift.endTime)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {shift.status === 'draft' && (
                      <>
                        <Tooltip title="Edit Shift" arrow>
                          <IconButton
                            onClick={() => navigate(`/shift-management/shifts/${shift.id}/edit`)}
                            className="bg-white/20 hover:bg-white/30 border border-white/30"
                            sx={{ color: 'white' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {isWithinShiftTimeWindow(shift) && (
                          <Tooltip title="Activate Shift" arrow>
                            <IconButton
                              onClick={async () => {
                                if (window.confirm('Are you sure you want to activate this shift?')) {
                                  try {
                                    await activateShiftMutation({
                                      variables: { input: { id: shift.id } }
                                    });

                                    toast.success('Shift activated successfully');
                                    await refetchShift();
                                  } catch (error: any) {
                                    console.error('Failed to activate shift', error);
                                    toast.error(error.message || 'Failed to activate shift. Please try again.');
                                  }
                                }
                              }}
                              disabled={activating}
                              className="bg-white/20 hover:bg-white/30 border border-white/30"
                              sx={{ color: 'white' }}
                            >
                              {activating ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <PlayArrowIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                    {(shift.status === 'draft' || shift.status === 'active') && isWithinShiftTimeWindow(shift) && (
                      <Tooltip title="Cancel Shift" arrow>
                        <IconButton
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to cancel this shift? This will cancel all pending/confirmed assignments.')) {
                              try {
                                await cancelShiftMutation({
                                  variables: { input: { id: shift.id } }
                                });

                                toast.success('Shift cancelled successfully');
                                await refetchShift();
                              } catch (error: any) {
                                console.error('Failed to cancel shift', error);
                                toast.error(error.message || 'Failed to cancel shift. Please try again.');
                              }
                            }
                          }}
                          disabled={cancelling}
                          className="bg-white/20 hover:bg-white/30 border border-white/30"
                          sx={{ color: 'white' }}
                        >
                          {cancelling ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <StopIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap mb-6">
            <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center shadow-sm">
                <PeopleIcon sx={{ fontSize: '20px', color: '#3B82F6' }} />
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">Assigned Drivers</p>
                <p className="text-xl font-bold text-gray-900">{assignedDrivers.length} / {maxDrivers}</p>
              </div>
            </div>

            {totalDriverStats > 0 && (
              <div className="bg-gradient-to-br from-white to-green-50/30 rounded-xl border border-gray-200/60 shadow-sm hover:shadow-md px-4 py-3 flex items-center gap-3 min-w-[140px] transition-all">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center shadow-sm">
                  <CheckCircleIcon sx={{ fontSize: '20px', color: '#10B981' }} />
                </div>
                <div>
                  <p className="text-xs text-gray-600 font-medium">Compliance Rate</p>
                  <p className="text-xl font-bold text-gray-900">{complianceRate}%</p>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-2 space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primaryColor to-darkBlue flex items-center justify-center">
                    <ScheduleIcon sx={{ fontSize: '18px', color: 'white' }} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Shift Session Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-1.5 mb-1">
                      <TagIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">ID</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{shift.id}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-1.5 mb-1">
                      <PlayArrowIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Start Time</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{formatDateTime(shift.startTime)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-1.5 mb-1">
                      <StopIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">End Time</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{formatDateTime(shift.endTime)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-1.5 mb-1">
                      <TimerIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Duration</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">{shiftDuration.toFixed(1)} hours</p>
                  </div>
                  {shift.cityId && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-1">
                        <LocationIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">City</span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">
                        {shiftData?.fetchShift?.city?.name || cityOptions.find(c => String(c.id) === String(shift.cityId))?.name || 'N/A'}
                      </p>
                    </div>
                  )}
                  {(shift.neighborhoodId || shift.zone) && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-1">
                        <LocationIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Neighborhood</span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">
                        {shiftData?.fetchShift?.neighborhood?.name || shift.zone || 'N/A'}
                      </p>
                    </div>
                  )}
                  {shiftData?.fetchShift?.shiftTemplate && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-1">
                        <CalendarIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Shift Schedule</span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">
                        {shiftData.fetchShift.shiftTemplate.name}
                      </p>
                    </div>
                  )}
                  {shift.startLatitude && shift.startLongitude && (
                    <>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-1.5 mb-1">
                          <LocationIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Latitude</span>
                        </div>
                        <p className="text-sm font-bold text-gray-900">{shift.startLatitude}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-1.5 mb-1">
                          <LocationIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Longitude</span>
                        </div>
                        <p className="text-sm font-bold text-gray-900">{shift.startLongitude}</p>
                      </div>
                    </>
                  )}
                  {shift.createdAt && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-1">
                        <CalendarIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Created At</span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">{formatDateTime(shift.createdAt)}</p>
                    </div>
                  )}
                  {shift.updatedAt && (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center gap-1.5 mb-1">
                        <UpdateIcon sx={{ fontSize: '16px', color: '#6B7280' }} />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Updated At</span>
                      </div>
                      <p className="text-sm font-bold text-gray-900">{formatDateTime(shift.updatedAt)}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
                      <PeopleIcon sx={{ fontSize: '20px', color: 'white' }} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Assigned Drivers</h3>
                    <span className="px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold">
                      {assignedDrivers.length} / {maxDrivers}
                    </span>
                  </div>
                </div>

                {assignedDrivers.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <PeopleIcon sx={{ fontSize: '56px', color: '#9CA3AF', marginBottom: '12px' }} />
                    <p className="text-sm font-semibold text-gray-600 mb-1">No drivers assigned</p>
                    <p className="text-xs text-gray-500">Assign drivers to this shift to get started</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b-2 border-gray-300">
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Driver</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Contact</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Assignment Status</th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Checked In At</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {assignedDrivers.map((driver, index) => {
                          const assignment = shiftAssignments.find((a) => a.driverId === driver.id);
                          const bgColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50';
                          const checkedInDate = assignment && assignment.checkedInAt
                            ? formatDateTime(assignment.checkedInAt)
                            : 'N/A';

                          return (
                            <tr
                              key={driver.id}
                              className={`${bgColor} hover:bg-gray-100 transition-colors duration-150`}
                            >
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primaryColor to-darkBlue flex items-center justify-center text-white font-bold text-sm shadow-md">
                                    {driver.name.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <div className="text-sm font-bold text-gray-900">{driver.name}</div>
                                    <div className="text-xs text-gray-500 font-mono">ID: {driver.id.slice(0, 8)}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {driver.phone ? (
                                  <div className="flex items-center gap-1.5 text-sm text-gray-700">
                                    <PhoneIcon sx={{ fontSize: '14px', color: '#6B7280' }} />
                                    <span className="font-medium">{driver.phone}</span>
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-400">N/A</span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold border ${assignment?.status === 'checked_in'
                                      ? 'bg-blue-100 text-blue-800 border-blue-300'
                                      : assignment?.status === 'completed'
                                        ? 'bg-green-100 text-green-800 border-green-300'
                                        : assignment?.status === 'confirmed'
                                          ? 'bg-purple-100 text-purple-800 border-purple-300'
                                          : 'bg-gray-100 text-gray-800 border-gray-300'
                                    }`}
                                >
                                  {assignment?.status ? assignment.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Pending'}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{checkedInDate}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {shiftComplianceReports.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                      <TrendingUpIcon sx={{ fontSize: '18px', color: 'white' }} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Compliance Reports</h3>
                    <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-bold">
                      {shiftComplianceReports.length}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-300">
                          <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-700 uppercase tracking-wider">Driver</th>
                          <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-700 uppercase tracking-wider">Online Duration</th>
                          <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-700 uppercase tracking-wider">Acceptance Rate</th>
                          <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-700 uppercase tracking-wider">Compliance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {shiftComplianceReports.map((report, index) => {
                          const driver = drivers.find((d) => d.id === report.driverId);

                          return (
                            <tr
                              key={report.id}
                              className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                              style={{ transition: 'background-color 0.2s' }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#FFFFFF' : '#F9FAFB'}
                            >
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primaryColor to-darkBlue flex items-center justify-center text-white font-bold text-xs">
                                    {driver?.name?.charAt(0) || '?'}
                                  </div>
                                  <div>
                                    <div className="text-sm font-bold text-gray-900">{driver?.name || 'Unknown'}</div>
                                    <div className="text-xs text-gray-500">{report.driverId}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                                {report.onlineDurationHours.toFixed(1)}h
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                                {report.acceptanceRate.toFixed(1)}%
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`text-sm font-bold ${!report.compliant ? 'text-red-600' : 'text-green-600'}`}>
                                  {!report.compliant ? 'Non-Compliant' : 'Compliant'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {shiftComplianceReports.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-md p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                      <CheckCircleIcon sx={{ fontSize: '18px', color: 'white' }} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Compliance</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-gray-700">Compliant</span>
                        <span className="text-base font-bold text-green-600">
                          {compliantCount} ({complianceRate}%)
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-500"
                          style={{ width: `${complianceRate}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-gray-700">Non-Compliant</span>
                        <span className="text-base font-bold text-red-600">
                          {nonCompliantCount} ({100 - complianceRate}%)
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full transition-all duration-500"
                          style={{ width: `${100 - complianceRate}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 shadow-md p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center">
                    <AccessTimeIcon sx={{ fontSize: '18px', color: 'white' }} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Quick Stats</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-gray-200">
                    <span className="text-xs font-medium text-gray-600">Shift Duration</span>
                    <span className="text-sm font-bold text-gray-900">{shiftDuration.toFixed(1)}h</span>
                  </div>
                  <div className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-gray-200">
                    <span className="text-xs font-medium text-gray-600">Total Assignments</span>
                    <span className="text-sm font-bold text-gray-900">{shiftAssignments.length}</span>
                  </div>
                  {totalDriverStats > 0 && (
                    <div className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-gray-200">
                      <span className="text-xs font-medium text-gray-600">Compliance Rate</span>
                      <span className={`text-sm font-bold ${complianceRate >= 90 ? 'text-green-600' : 'text-red-600'}`}>
                        {complianceRate}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShiftDetails;
